import { useState } from "react";
import {
  useMonitors,
  useMonitor,
  useOpportunities,
  useCreateMonitor,
  useTriggerScan,
  useUpdateOpportunity,
  useRedraftOpportunity,
} from "../../hooks/useMonitor";
import { OpportunityCard } from "./OpportunityCard";
import { MonitorSetup } from "./MonitorSetup";
import type { MonitorPlatform } from "../../../../shared/types";

interface Props {
  brandId: string;
}

type FilterStatus = "pending" | "posted" | "skipped" | "all";
type SortBy = "score" | "recency" | "engagement";

export function ConversationMonitor({ brandId }: Props) {
  const [filterStatus, setFilterStatus] = useState<FilterStatus>("pending");
  const [filterPlatform, setFilterPlatform] = useState<MonitorPlatform | "all">("all");
  const [sortBy, setSortBy] = useState<SortBy>("score");
  const [showSetup, setShowSetup] = useState(false);
  const [redraftingId, setRedraftingId] = useState<string | null>(null);

  const { data: monitorsData, isLoading: loadingMonitors } = useMonitors(brandId);
  const monitors = monitorsData?.data ?? [];
  const activeMonitor = monitors[0]; // One monitor per brand for now

  const { data: monitorDetail } = useMonitor(activeMonitor?.id);
  const monitor = monitorDetail?.data;

  const { data: oppsData, isLoading: loadingOpps } = useOpportunities(activeMonitor?.id, {
    status: filterStatus === "all" ? undefined : filterStatus,
    platform: filterPlatform === "all" ? undefined : filterPlatform,
    sort: sortBy,
  });
  const opportunities = oppsData?.data ?? [];

  const createMonitor = useCreateMonitor();
  const triggerScan = useTriggerScan(activeMonitor?.id || "");
  const updateOpp = useUpdateOpportunity(activeMonitor?.id || "");
  const redraftOpp = useRedraftOpportunity(activeMonitor?.id || "");

  // ── No monitor yet → show setup ──
  if (!loadingMonitors && monitors.length === 0) {
    return (
      <MonitorSetup
        brandId={brandId}
        onSubmit={async (config) => {
          await createMonitor.mutateAsync(config);
        }}
        isCreating={createMonitor.isPending}
      />
    );
  }

  if (loadingMonitors) {
    return (
      <div className="flex justify-center py-12">
        <div className="h-6 w-6 animate-spin rounded-full border-2 border-indigo-600 border-t-transparent" />
      </div>
    );
  }

  const handleMarkPosted = (oppId: string) => {
    updateOpp.mutate({ oppId, status: "posted" });
  };

  const handleSkip = (oppId: string) => {
    updateOpp.mutate({ oppId, status: "skipped" });
  };

  const handleEditDraft = (oppId: string, newDraft: string) => {
    updateOpp.mutate({ oppId, draft_response: newDraft });
  };

  const handleRedraft = async (oppId: string) => {
    setRedraftingId(oppId);
    try {
      await redraftOpp.mutateAsync(oppId);
    } finally {
      setRedraftingId(null);
    }
  };

  const lastScanTime = monitor?.last_scanned_at
    ? new Date(monitor.last_scanned_at).toLocaleString()
    : "Never";

  const latestScan = monitor?.latest_scan;
  const isScanning = latestScan?.status === "running" || triggerScan.isPending;

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <div className="text-xs text-slate-400">
            Last scan: {lastScanTime}
            {latestScan?.status === "completed" && (
              <span className="ml-2">
                ({latestScan.threads_found} found, {latestScan.threads_above_threshold} opportunities)
              </span>
            )}
          </div>
        </div>
        <div className="flex gap-2">
          <button
            onClick={() => setShowSetup(!showSetup)}
            className="rounded-lg bg-slate-100 px-3 py-1.5 text-xs font-medium text-slate-600 hover:bg-slate-200"
          >
            Settings
          </button>
          <button
            onClick={() => triggerScan.mutate()}
            disabled={isScanning}
            className="rounded-lg bg-indigo-600 px-3 py-1.5 text-xs font-medium text-white hover:bg-indigo-700 disabled:opacity-50"
          >
            {isScanning ? "Scanning..." : "Scan Now"}
          </button>
        </div>
      </div>

      {/* Settings panel (collapsible) */}
      {showSetup && activeMonitor && (
        <div className="rounded-lg border border-slate-200 bg-slate-50 p-4">
          <div className="mb-2 text-xs font-medium text-slate-500">Monitor Settings</div>
          <div className="space-y-2 text-sm">
            <div>
              <span className="text-slate-400">Platforms:</span>{" "}
              <span className="text-slate-700">{(activeMonitor.platforms || []).join(", ")}</span>
            </div>
            <div>
              <span className="text-slate-400">Keywords:</span>{" "}
              <span className="text-slate-700">{(activeMonitor.keywords || []).join(", ") || "Auto-generated"}</span>
            </div>
            <div>
              <span className="text-slate-400">Competitors:</span>{" "}
              <span className="text-slate-700">{(activeMonitor.competitor_names || []).join(", ") || "None"}</span>
            </div>
            <div>
              <span className="text-slate-400">Frequency:</span>{" "}
              <span className="text-slate-700">{activeMonitor.scan_frequency}</span>
            </div>
          </div>
        </div>
      )}

      {/* Filters */}
      <div className="flex flex-wrap gap-2">
        <div className="flex rounded-lg bg-slate-100 p-0.5">
          {(["pending", "posted", "skipped", "all"] as FilterStatus[]).map((s) => (
            <button
              key={s}
              onClick={() => setFilterStatus(s)}
              className={`rounded-md px-2.5 py-1 text-xs font-medium transition-colors ${
                filterStatus === s ? "bg-white text-slate-900 shadow-sm" : "text-slate-500 hover:text-slate-700"
              }`}
            >
              {s === "all" ? "All" : s.charAt(0).toUpperCase() + s.slice(1)}
            </button>
          ))}
        </div>

        <select
          value={filterPlatform}
          onChange={(e) => setFilterPlatform(e.target.value as MonitorPlatform | "all")}
          className="rounded-lg border border-slate-200 bg-white px-2.5 py-1 text-xs text-slate-600"
        >
          <option value="all">All platforms</option>
          <option value="reddit">Reddit</option>
          <option value="twitter">Twitter/X</option>
          <option value="hackernews">Hacker News</option>
          <option value="linkedin">LinkedIn</option>
          <option value="quora">Quora</option>
          <option value="indiehackers">Indie Hackers</option>
          <option value="producthunt">Product Hunt</option>
        </select>

        <select
          value={sortBy}
          onChange={(e) => setSortBy(e.target.value as SortBy)}
          className="rounded-lg border border-slate-200 bg-white px-2.5 py-1 text-xs text-slate-600"
        >
          <option value="score">Sort by score</option>
          <option value="recency">Sort by recency</option>
          <option value="engagement">Sort by engagement</option>
        </select>
      </div>

      {/* Opportunities list */}
      {loadingOpps ? (
        <div className="flex justify-center py-8">
          <div className="h-6 w-6 animate-spin rounded-full border-2 border-indigo-600 border-t-transparent" />
        </div>
      ) : opportunities.length === 0 ? (
        <div className="rounded-lg border border-dashed border-slate-200 py-12 text-center">
          <div className="text-sm text-slate-400">
            {filterStatus !== "all" || filterPlatform !== "all"
              ? "No opportunities match your filters"
              : "No opportunities yet. Run a scan to discover conversations."}
          </div>
        </div>
      ) : (
        <div className="space-y-3">
          {opportunities.map((opp) => (
            <OpportunityCard
              key={opp.id}
              opportunity={opp}
              onMarkPosted={handleMarkPosted}
              onSkip={handleSkip}
              onEditDraft={handleEditDraft}
              onRedraft={handleRedraft}
              isRedrafting={redraftingId === opp.id}
            />
          ))}
        </div>
      )}
    </div>
  );
}
